/* Just Silver (08 CONCEPT) - Load more that remembers where you were.

   The theme's original version rewrote the address to ?page=N on every Load more
   and again when a product was opened, so the back button returned to page N
   alone: the first forty pieces were gone and a "Load previous" button sat at the
   top. This version keeps the address as it was when the page opened, remembers
   which pages have been loaded and how far down the shopper was, and on return
   loads those pages again and scrolls back to the same spot. Without JavaScript
   the theme's own numbered pagination still works. */
class CollectionInfiniteScroll extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClick.bind(this));
    this.container = document.getElementById('ProductGrid');
    this.isLoading = false;

    const params = new URLSearchParams(window.location.search);
    this.firstPage = parseInt(params.get('page')) || 1;
    this.page = this.firstPage;
    this.lastPage = this.firstPage;

    this.memoryKey = 'jsq-grid:' + window.location.pathname + '?' + this.cleanParams().toString();

    const ready = () => {
      const hash = window.location.hash;
      if (hash && document.querySelector(hash)) {
        setTimeout(() => document.querySelector(hash).closest('.card-product').scrollIntoView(), 50);
      }
      this.restore();
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
    else ready();
  }

  cleanParams() {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.delete('section_id');
    params.sort();
    return params;
  }

  readMemory() {
    try {
      const raw = sessionStorage.getItem(this.memoryKey);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  writeMemory(data) {
    try {
      if (data) sessionStorage.setItem(this.memoryKey, JSON.stringify(data));
      else sessionStorage.removeItem(this.memoryKey);
    } catch (e) { /* private mode: nothing to remember */ }
  }

  onClick(event) {
    if (this.isLoading) return;

    // A product link: remember the loaded range and the scroll position.
    const link = event.target.closest('a[data-product-page]');
    if (link) {
      this.writeMemory({
        first: this.firstPage,
        last: this.lastPage,
        y: window.scrollY,
        id: link.getAttribute('id'),
        t: Date.now()
      });
      return;
    }

    const button = event.target.closest('[data-load-id]');
    if (!button) return;
    event.preventDefault();
    const id = button.getAttribute('data-load-id');
    const direction = button.getAttribute('data-load-direction');
    const target = direction === 'more' ? this.lastPage + 1 : this.firstPage - 1;
    if (target < 1) return;
    this.setLoading();
    this.fetchPage(id, target, direction).then(() => {
      if (direction === 'more') this.lastPage = target;
      else this.firstPage = target;
      this.isLoading = false;
      this.clearLoading();
    }).catch(() => {
      this.isLoading = false;
      this.clearLoading();
      document.dispatchEvent(new CustomEvent('rendering:error'));
    });
  }

  pageUrl(page) {
    const params = this.cleanParams();
    if (page > 1) params.set('page', page);
    const qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  fetchPage(id, page, direction, focus = true) {
    const url = this.pageUrl(page) + (this.pageUrl(page).includes('?') ? '&' : '?') + 'section_id=' + id;
    return fetch(url)
      .then((response) => response.text())
      .then((html) => this.renderData(html, direction, focus));
  }

  setLoading() {
    this.querySelectorAll('[data-load-id]').forEach((button) => {
      button.setAttribute('aria-disabled', true);
      button.setAttribute('data-loading', true);
    });
    this.isLoading = true;
  }

  clearLoading() {
    this.querySelectorAll('[data-load-id]').forEach((button) => {
      button.removeAttribute('data-loading');
      button.removeAttribute('aria-disabled');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.add('hidden');
    });
  }

  renderData(html, direction, focus) {
    const parser = new DOMParser().parseFromString(html, 'text/html');
    const grid = parser.getElementById('ProductGrid');
    if (!grid) throw new Error('no grid');
    const firstElement = grid.querySelector('.card-product a');
    const firstElementLink = firstElement ? firstElement.getAttribute('href') : null;
    let htmlPosition = 'beforeend';
    let paginationId = 'ProductLoadMore';
    if (direction === 'previous') {
      htmlPosition = 'afterbegin';
      paginationId = 'ProductLoadPrevious';
    }
    const paginationEl = parser.getElementById(paginationId);
    const mine = this.querySelector('#' + paginationId);
    if (mine) {
      if (paginationEl) mine.innerHTML = paginationEl.innerHTML;
      else mine.style.display = 'none';
    }
    this.container.insertAdjacentHTML(htmlPosition, grid.innerHTML);
    if (focus && firstElementLink) {
      const el = this.querySelector('a[href="' + firstElementLink + '"]');
      if (el) el.focus({ preventScroll: true });
    }
  }

  /* On return from a product (or a reload), load the pages that were open
     before and go back to the same place. */
  restore() {
    const memory = this.readMemory();
    if (!memory) return;
    this.writeMemory(null);
    // Only within the same visit, and only when this is the first page again.
    if (Date.now() - memory.t > 30 * 60 * 1000) return;
    if (memory.first !== this.firstPage || memory.last <= this.firstPage) return;
    const id = this.container && this.container.getAttribute('data-id');
    if (!id) return;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    this.setLoading();
    const pages = [];
    for (let p = this.firstPage + 1; p <= memory.last; p++) pages.push(p);
    const run = pages.reduce((chain, p) => chain.then(() => this.fetchPage(id, p, 'more', false)), Promise.resolve());
    run.then(() => {
      this.lastPage = memory.last;
      this.isLoading = false;
      this.clearLoading();
      const target = memory.id && document.getElementById(memory.id);
      if (target && target.closest('.card-product')) {
        target.closest('.card-product').scrollIntoView({ block: 'center' });
      } else {
        window.scrollTo(0, memory.y || 0);
      }
    }).catch(() => {
      this.isLoading = false;
      this.clearLoading();
    });
  }
}
customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
